import { useFetchProfile } from '@/hooks/useFetchProfile'
import { normalizeUrl } from '@/lib/url'
import client from '@/services/client.service'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { useAtomValue } from 'jotai'
import { invalidateFavorites, favoritesChannelAtom } from '@/atoms/invalidation'
import { fetchFavoriteRelaysWithSets, loadFavoriteRelays } from '@nostr/gadgets/lists'
import { ResolvedSet } from '@nostr/gadgets/sets'
import { forwardRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAsync } from 'react-use'
import { decode } from '@nostr/tools/nip19'
import RelaySetCard from '@/components/RelaySetCard'
import { now } from '@/lib/utils'
import { useCurrentPubkey } from '@/atoms/session'
import { sampleTopFromFollows } from '@/lib/recommendations'
import { unwrappedFavoriteRelaysWithSetsAtom } from '@/atoms/lists'
import EditableList from '@/components/EditableList'
import RelayCard from '@/components/RelayCard'
import ListEventMenu from '@/components/ListEventMenu'
import * as kinds from '@nostr/tools/kinds'

const FavoriteRelaysPage = forwardRef(({ id, index }: { id: string; index?: number }, ref) => {
  const { t } = useTranslation()
  const currentPubkey = useCurrentPubkey()
  const { profile } = useFetchProfile(id)
  const channel = useAtomValue(favoritesChannelAtom)
  const ourFavorites = useAtomValue(unwrappedFavoriteRelaysWithSetsAtom)

  useEffect(() => {
    if (!profile) return
    const name = profile.shortName || '...'
    document.title = t("{{name}}'s favorite relays on {{appName}}", {
      name,
      appName: window.hallway.universe.appName
    })
    history.replaceState(null, '')
  }, [profile])

  const { value: favorites } = useAsync(
    async () =>
      profile
        ? profile.pubkey === currentPubkey
          ? ourFavorites
          : fetchFavoriteRelaysWithSets(profile.pubkey)
        : [],
    [profile?.pubkey, channel]
  )

  async function loadRecommendations(): Promise<string[]> {
    if (!currentPubkey || !favorites) return []

    const existingFavorites: string[] = []
    for (const f of favorites) {
      if (typeof f === 'string') existingFavorites.push(f)
    }
    const existing = new Set(existingFavorites.map(normalizeUrl))
    const fromFollows = await sampleTopFromFollows<string>(
      currentPubkey,
      async (pk) => {
        const out: string[] = []
        for (const item of (await loadFavoriteRelays(pk)).items) {
          if (typeof item === 'string') out.push(item)
        }
        return out
      },
      (url) => normalizeUrl(url),
      existing
    )
    const universe = window.hallway.universe.defaultFavoriteRelays
      .map(normalizeUrl)
      .filter((u) => !existing.has(u) && !fromFollows.includes(u))
    return [...fromFollows, ...universe]
  }

  return (
    <SecondaryPageLayout
      ref={ref}
      index={index}
      controls={
        profile && (
          <ListEventMenu
            pointer={{ kind: kinds.FavoriteRelays, pubkey: profile.pubkey, identifier: '' }}
          />
        )
      }
      title={
        profile && profile.pubkey === currentPubkey
          ? t("{{username}}'s favorite relay feeds", { username: profile.shortName })
          : t('Favorite Relay Feeds')
      }
    >
      <div className="px-4 py-3">
        <EditableList
          items={favorites || []}
          editable={profile && currentPubkey === profile.pubkey}
          renderItem={(relayOrSet) =>
            typeof relayOrSet === 'string' ? (
              <RelayCard url={relayOrSet} />
            ) : (
              <RelaySetCard relaySet={relayOrSet} select={false} onSelectChange={() => {}} />
            )
          }
          validateItem={(val) => {
            try {
              const { type, data } = decode(val)
              if (type === 'naddr')
                return {
                  ok: true,
                  item: { items: [], pointer: data, event: null, title: '' } as ResolvedSet<string>
                }
              return {
                ok: false,
                error: t('Must be a naddr pointer, not {{actual}}', { actual: type })
              }
            } catch {
              /***/
            }

            const url = normalizeUrl(val)
            if (!url) return { ok: false as const, error: t('Invalid relay URL') }
            return { ok: true as const, item: url }
          }}
          onUpdate={async (newItems: (string | ResolvedSet<string>)[]) => {
            if (!currentPubkey) return

            const tags: string[][] = []
            for (const urlOrSet of newItems) {
              if (typeof urlOrSet === 'string') {
                tags.push(['relay', urlOrSet])
              } else {
                tags.push([
                  'a',
                  `${urlOrSet.pointer.kind}:${urlOrSet.pointer.pubkey}:${urlOrSet.pointer.identifier}`,
                  ...(urlOrSet.items?.length ? urlOrSet.items[0] : [])
                ])
              }
            }
            const draft = {
              kind: kinds.FavoriteRelays,
              content: '',
              tags,
              created_at: now()
            }

            const event = await client.publish(draft)
            if (!event) {
              console.warn('failed to publish updated favorite relays event')
              toast.error(t('Failed to publish updated favorite relays event.'))
              return
            }

            await loadFavoriteRelays(event.pubkey, undefined, event)
            invalidateFavorites()
          }}
          placeholder={t('Enter relay URL or relay set naddr')}
          emptyMessage={t('No favorite relays.')}
          loadRecommendations={loadRecommendations}
        />
      </div>
    </SecondaryPageLayout>
  )
})
FavoriteRelaysPage.displayName = 'FavoriteRelaysPage'
export default FavoriteRelaysPage
